// #region Imports
import { Context, Dispatch, ReactNode } from 'react';
import { ActionMap, GoMotoComponent, GoMotoFunction } from '../../API/GoMoto.types';
import { ILockerFixture } from '../lockers';
import { LockerActionType } from '../Appointment/appointment.types';
import { IAppointment } from '../../API/GoMoto';
// #endregion

// #region Setup
/**
 * The object type that is maintained by the AdminLockerOperation context and can be
 * consumed by any child of the AdminLockerOperation provider.
 */
export interface AdminLockerOperationContextState {
    /**
     * The type of locker operation being performed when we arrive at the LockerOpen component.
     */
    lockerOpType: Exclude<LockerActionType, 'store-key' | 'pickup-and-exchange-key'>;
    /**
     * The locker ID of the locker that we are going to open during an `'open-locker'`
     * locker operation on a locker with no key from an appointment stored inside,
     * and/or the locker number we will display at the LockerOpen component.
     */
    lockerId?: ILockerFixture['Id'];
    /**
     * The path that we will route back to after finishing at the LockerOpen screen.
     */
    returnPath?: string;
    /**
     * Whether we should trigger an API call to get the updated appointment after we
     * finish a locker operation.
     */
    repullAppointment: boolean;
    /**
     * Whether the appointment and subsequent locker operation that is being performed
     * will be done through the Create tab in Key Management and we should treat the
     * the new appointment and subsequent locker operation as a new pickup appointment.
     */
    isAdminCreate: boolean;
    /**
     * Whether a store-key locker operation has just completed. Set when `StoreKey` finishes
     * storing a key so that the Loaded Keys tab can schedule a delayed re-fetch on its next mount.
     *
     * The backend exhibits read-after-write lag for a freshly stored key, so the immediate fetch
     * on mount can miss the just-stored tile. The flag survives the route unmount of
     * `KeyManagementRoutes/*` because this provider sits above it.
     */
    keyStored: boolean;
}

/**
 * The type for the dispatch provider as well as the dispatch function itself.
 */
export type AdminLockerOperationDispatchState = Dispatch<AdminLockerOperationActions>;

/**
 * The context type for the AdminLockerOperation state context. It can be undefined so that
 * we can verify anything attempting to consume the context is a child of the context.
 */
export type AdminLockerOperationState = AdminLockerOperationContextState | undefined;

/**
 * The context type for the AdminLockerOperation dispatch context. It can be undefined so that
 * we can verify anything attempting to use the dispatch is a child of the context.
 */
export type AdminLockerOperationDispatch = AdminLockerOperationDispatchState | undefined;

/**
 * The AdminLockerOperation state context type that is returned after the creation of the state context.
 */
export type AdminLockerOperationContextType = Context<AdminLockerOperationState>;

/**
 * The AdminLockerOperation dispatch context type that is returned after the creation of the dispatch context.
 */
export type AdminLockerOperationDispatchContextType = Context<AdminLockerOperationDispatch>;
// #endregion

// #region Reducer
/**
 * The AdminLockerOperation dispatch types used by our {@link ActionMap} type to distinguish what payload the
 * dispatch action will require.
 */
export enum AdminLockerOperationDispatchTypes {
    /**
     * Dispatches an action to set the locker operation type and also the Locker ID if needed.
     *
     * @note This dispatch is only relevant for a locker operation that will direct the user to the LockerOpen screen and
     * multiple states need to be preserved from other components in the Key Management routing.
     */
    SetLockerOperation = 'SET_LOCKER_OPERATION',
    /**
     * Dispatches an action to set the return path of the locker operation so we can re-route back to the direct route
     * after finishing the locker operation.
     */
    SetReturnPath = 'SET_RETURN_PATH',
    /**
     * Dispatches an action to set the isAdminCreate flag to indicate that the appointment and subsequent locker operation
     * should be treated as a new pickup appointment.
     *
     * @note This dispatch is only relevant for when a user clicks on the Create tab to start a new pickup appointment.
     */
    SetIsAdminCreate = 'SET_IS_ADMIN_CREATE',
    /**
     * Dispatches an action to set the repullAppointment flag to indicate that once we finish the locker operation, we
     * should fire an API call to grab the now updated appointment from the server.
     *
     * @note This dispatch is only relevant for the Appointment Details screen.
     */
    SetRepullAppointment = 'SET_REPULL_APPOINTMENT',
    /**
     * Dispatches an action to flag that a store-key locker operation just completed, used by the Loaded Keys
     * tab to schedule a delayed re-fetch on its next mount so it picks up the freshly stored key after the
     * backend has had time to propagate.
     */
    SetKeyStored = 'SET_KEY_STORED',
    /**
     * Dispatches an action to reset the entire context to the initialized state.
     *
     * @note This occurs after every locker operation is performed in Key Management, except for The 24/7 locker operations
     * that occur directly at the Appointment Details and Key Map screens.
     */
    Reset = 'RESET'
}

/**
 * An object mapping each dispatch type to the payload required for that dispatch action.
 */
export type AdminLockerOperationPayload = {
    [AdminLockerOperationDispatchTypes.SetLockerOperation]: {
        lockerOpType: Exclude<AdminLockerOperationContextState['lockerOpType'], 'not-started'>;
        lockerId?: AdminLockerOperationContextState['lockerId'];
    };
    [AdminLockerOperationDispatchTypes.SetReturnPath]: Exclude<AdminLockerOperationContextState['returnPath'], undefined>;
    [AdminLockerOperationDispatchTypes.SetIsAdminCreate]: Exclude<AdminLockerOperationContextState['isAdminCreate'], undefined>;
    [AdminLockerOperationDispatchTypes.SetRepullAppointment]: Exclude<AdminLockerOperationContextState['repullAppointment'], undefined>;
    [AdminLockerOperationDispatchTypes.SetKeyStored]: Exclude<AdminLockerOperationContextState['keyStored'], undefined>;
    [AdminLockerOperationDispatchTypes.Reset]: never;
};

/**
 * The union type of dispatch actions that results from using {@link ActionMap}.
 */
export type AdminLockerOperationActions = ActionMap<AdminLockerOperationPayload>[keyof ActionMap<AdminLockerOperationPayload>];

/**
 * The AdminLockerOperation reducer function type definition. Primarily used by any `useReducer`
 * that uses the function or the function itself.
 */
export type AdminLockerOperationReducerFunction = GoMotoFunction<
    [AdminLockerOperationContextState, AdminLockerOperationActions],
    AdminLockerOperationContextState
>;
// #endregion

// #region Provider
/**
 * The object types for the props of the AdminLockerOperation provider component.
 */
export interface AdminLockerOperationProviderProps {
    /**
     * Any element that will render as a child of the AdminLockerOperation context.
     */
    children?: ReactNode;
}
// #endregion

// #region Union Types
/**
 * Union type for the different dialogs that we would want to show based on the appointment state
 * of the appointment stored in a locker or appointment we are viewing in the appointment details screen.
 */
export type AdminKeyDropDialogVariants = 'payment-required' | 'open-locker' | 'return-key';
// #endregion

// #region Objects
/**
 * Type definition for the usMzeroAdminKeyDrop hook return. This hook only handles logic specific to
 * The 24/7 currently.
 *
 * This logic is separated from the other KeyDrop hook so that it is easy to remove
 * when we end support for Mzero machines.
 *
 * @note All props on this object resolve to null if the kiosk is not a 24/7 kiosk.
 */
export interface MzeroAdminKeyDropReturn {
    /**
     * Callback function for when the avisor begins the retrieve key process on a 24/7 kiosk.
     */
    onMzeroRetrieveKeyClick: GoMotoFunction<[IAppointment | undefined], Promise<void>> | null;
    /**
     * Dialog components that will display after the key retrieve process has begun.
     */
    MzeroAdminKeyDrop: GoMotoComponent;
}

/**
 * Type definition for the useAdminKeyDropProcess hook props.
 */
export interface AdminKeyDropProcessProps {
    /**
     * Callback function to handle navigating once we proceed in a dialog.
     */
    onProceed: VoidFunction;
    /**
     * Callback function to set a state indicating whether the mzero locker has completed the retrieval process
     */
    setMzeroRetrieveComplete?: GoMotoFunction<[boolean], void>;
}

/**
 * Type definition for the useAdminKeyDropProcess hook return object.
 *
 * @extends MzeroAdminKeyDropReturn - We only partially extend the object for the {@link MzeroAdminKeyDropReturn.onMzeroRetrieveKeyClick onMzeroRetrieveKeyClick} type.
 */
export interface AdminKeyDropProcessReturn extends Pick<MzeroAdminKeyDropReturn, 'onMzeroRetrieveKeyClick'> {
    /**
     * The component that contains all of the necessary dialogs for any of the non-24/7 kiosks when retrieving a key or
     * opening a locker.
     */
    AdminKeyDropProcess: GoMotoComponent;
    /**
     * Callback dispatch to set the locker ID state inside of the hook. This is primarily used so that we can display the
     * locker ID on the LockerOpen component screen in the localization text.
     */
    setLockerId: Dispatch<ILockerFixture['Id']>;
    /**
     * Callback function to toggle which dialog to show based on logic in the component the hook is being used in.
     */
    showKeyDropProcessDialog: GoMotoFunction<[AdminKeyDropDialogVariants], void>;
}
// #endregion

// #region Hooks
/**
 * The function type for the `useAdminState` hook. Takes no arguments and returns the current AdminLockerOperation state.
 */
export type AdminLockerOperationStateHook = GoMotoFunction<
    [void],
    AdminLockerOperationContextState
>;

/**
 * The function type for the `useAdminDispatch` hook. Takes no arguments and returns a dispatch function for the AdminLockerOperation context.
 */
export type AdminLockerOperationDispatchHook = GoMotoFunction<
    [void],
    AdminLockerOperationDispatchState
>;

/**
 * Type definition for the useMzeroAdminKeyDrop hook.
 *
 * @returns For details about the return object, click {@link MzeroAdminKeyDropReturn here}.
 */
export type MzeroAdminKeyDropHook = GoMotoFunction<[void], MzeroAdminKeyDropReturn>;

/**
 * Type definition for the useAdminKeyDropProcess hook.
 *
 * @returns For details about the return object, click {@link AdminKeyDropProcessReturn here}.
 */
export type AdminKeyDropProcessHook = GoMotoFunction<AdminKeyDropProcessProps, AdminKeyDropProcessReturn>;
// #endregion
